/**
 * Delete Task Operations
 * Handles single and batch task deletion with dependency cleanup.
 *
 * Supports options.bridge: when true, surviving tasks that depended on a
 * deleted node are rewired to the deleted node's own upstream dependencies
 * (preserving AND/OR condition semantics) instead of simply losing the edge.
 * Milestones anchored to a deleted host inherit a surviving neighbor so they
 * stay in the bridged chain.
 */
import { getDependencyTaskId } from '../utils';
import { removeTaskFromConditions, hasConditionForTask } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { computeBridgedConditionsForDeletion } from '../../../shared/bridgeDeleteConditions';
import { computeBridgedMilestoneAnchors } from '../../../shared/bridgeDeleteMilestones';
import { notifyInboxTargetsGone, notifyInboxTargetsRestored } from '../../../../../utils/notificationInbox';
import { collectCascadedNoteIds } from '../../../FlowchartView/utils/noteUtils';

/**
 * Filter unlockConditions - removes references to deleted task(s).
 * Handles both v1 (flat array) and v2 (block-based) formats.
 */
const filterUnlockConditions = (conditions, taskIdOrIds) => {
  if (!conditions) return conditions;
  const ids = Array.isArray(taskIdOrIds) ? taskIdOrIds : [taskIdOrIds];
  let result = conditions;
  for (const id of ids) {
    result = removeTaskFromConditions(result, id);
  }
  return result;
};

/**
 * Snapshot dependency-related fields of every surviving task that references
 * a deleted id (or is about to be bridge-patched), so undo can restore them.
 */
const snapshotAffectedTasks = (tasks, idsToDelete, bridgePatch, milestonePatch) => {
  return tasks
    .filter((t) => {
      if (idsToDelete.has(t.id)) return false;
      if (bridgePatch && bridgePatch[t.id]) return true;
      if (milestonePatch && milestonePatch[t.id]) return true;
      if (Array.isArray(t.dependsOn) && t.dependsOn.some((dep) => idsToDelete.has(getDependencyTaskId(dep)))) {
        return true;
      }
      return [...idsToDelete].some((id) => hasConditionForTask(t.unlockConditions, id));
    })
    .map((t) => ({
      id: t.id,
      dependsOn: Array.isArray(t.dependsOn) ? [...t.dependsOn] : t.dependsOn,
      unlockConditions: t.unlockConditions,
      milestoneAnchor: t.milestoneAnchor ? { ...t.milestoneAnchor } : t.milestoneAnchor,
    }));
};

/**
 * Produce the updated task list after removing deleted tasks and cleaning up
 * (or bridging) references to them.
 */
const applyDeletionToTasks = (tasks, idsToDelete, deletedIdList, bridgePatch, milestonePatch) => {
  return tasks
    .filter((t) => !idsToDelete.has(t.id))
    .map((t) => {
      let next = t;
      if (milestonePatch && milestonePatch[t.id]) {
        next = { ...next, milestoneAnchor: milestonePatch[t.id] };
      }
      if (bridgePatch && bridgePatch[t.id]) {
        // Bridged: new conditions become the source of truth
        return { ...next, dependsOn: [], unlockConditions: bridgePatch[t.id] };
      }
      return {
        ...next,
        dependsOn: Array.isArray(next.dependsOn)
          ? next.dependsOn.filter((dep) => !idsToDelete.has(getDependencyTaskId(dep)))
          : [],
        unlockConditions: filterUnlockConditions(next.unlockConditions, deletedIdList),
      };
    });
};

/** Restore snapshot fields onto restored task list (for undo). */
const restoreAffectedTasks = (tasks, affectedTasks) => {
  if (affectedTasks.length === 0) return tasks;
  return tasks.map((t) => {
    const affected = affectedTasks.find((a) => a.id === t.id);
    if (affected) {
      const restored = { ...t, dependsOn: affected.dependsOn, unlockConditions: affected.unlockConditions };
      if (affected.milestoneAnchor !== undefined) {
        restored.milestoneAnchor = affected.milestoneAnchor;
      }
      return restored;
    }
    return t;
  });
};

/**
 * Delete a single task
 * @param {string} taskId
 * @param {Object} [options]
 * @param {boolean} [options.bridge] - rewire downstream tasks to the deleted task's upstream dependencies
 */
export const createDeleteTaskHandler = (localSprint, setLocalSprint, recordAction) => {
  return (taskId, options = {}) => {
    const allTasks = localSprint?.tasks || [];
    const taskToDelete = allTasks.find(t => t.id === taskId);
    if (!taskToDelete) return;
    const campaignId = localSprint?.id;

    const idsToDelete = collectCascadedNoteIds(allTasks, [taskId]);
    const tasksToDelete = allTasks.filter((t) => idsToDelete.has(t.id));
    const deletedIdList = tasksToDelete.map((t) => t.id);
    const bridgePatch = options.bridge
      ? computeBridgedConditionsForDeletion(allTasks, [taskId])
      : null;
    const milestonePatch = options.bridge
      ? computeBridgedMilestoneAnchors(allTasks, idsToDelete)
      : null;

    const affectedTasks = snapshotAffectedTasks(allTasks, idsToDelete, bridgePatch, milestonePatch);

    const performDelete = () => {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: applyDeletionToTasks(prev.tasks || [], idsToDelete, deletedIdList, bridgePatch, milestonePatch),
        };
      });
    };

    performDelete();
    notifyInboxTargetsGone({ campaignId, missionIds: [taskId] });

    // Record for undo
    if (recordAction) {
      recordAction({
        type: 'DELETE_TASK',
        description: `Delete: "${taskToDelete.name}"`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            const tasks = restoreAffectedTasks([...prev.tasks, ...tasksToDelete], affectedTasks);
            return { ...prev, tasks };
          });
          notifyInboxTargetsRestored({ campaignId, missionIds: [taskId] });
        },
        redo: () => {
          performDelete();
          notifyInboxTargetsGone({ campaignId, missionIds: [taskId] });
        },
      });
    }
  };
};

/**
 * Delete multiple tasks at once
 * @param {Array<string>} taskIds
 * @param {Object} [options]
 * @param {boolean} [options.bridge] - rewire downstream tasks through the deleted set
 */
export const createDeleteTasksHandler = (localSprint, setLocalSprint, recordAction) => {
  return (taskIds, options = {}) => {
    if (!taskIds || taskIds.length === 0) return;

    const allTasks = localSprint?.tasks || [];
    const idsToDelete = collectCascadedNoteIds(allTasks, taskIds);
    const tasksToDelete = allTasks.filter(t => idsToDelete.has(t.id));
    if (tasksToDelete.length === 0) return;
    const campaignId = localSprint?.id;
    const deletedIds = tasksToDelete.map((t) => t.id);

    const bridgePatch = options.bridge
      ? computeBridgedConditionsForDeletion(allTasks, taskIds)
      : null;
    const milestonePatch = options.bridge
      ? computeBridgedMilestoneAnchors(allTasks, idsToDelete)
      : null;

    const affectedTasks = snapshotAffectedTasks(allTasks, idsToDelete, bridgePatch, milestonePatch);

    const performDelete = () => {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: applyDeletionToTasks(prev.tasks || [], idsToDelete, taskIds, bridgePatch, milestonePatch),
        };
      });
    };

    performDelete();
    notifyInboxTargetsGone({ campaignId, missionIds: deletedIds });

    // Record for undo
    if (recordAction) {
      recordAction({
        type: 'DELETE_TASKS',
        description: `Delete ${tasksToDelete.length} items`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            const tasks = restoreAffectedTasks([...prev.tasks, ...tasksToDelete], affectedTasks);
            return { ...prev, tasks };
          });
          notifyInboxTargetsRestored({ campaignId, missionIds: deletedIds });
        },
        redo: () => {
          performDelete();
          notifyInboxTargetsGone({ campaignId, missionIds: deletedIds });
        },
      });
    }
  };
};
